import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import {
  Search,
  RefreshCcw,
  Send,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  XCircle,
  Mail,
} from "lucide-react";

const STATUSES = ["all", "sent", "logged_no_smtp", "failed"];

const statusLabels = {
  sent: "Sent",
  logged_no_smtp: "Logged (no SMTP)",
  failed: "Failed",
};

const statusIcon = (s) => {
  if (s === "sent") return <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />;
  if (s === "failed") return <XCircle className="w-3.5 h-3.5 text-red-600" />;
  return <Clock className="w-3.5 h-3.5 text-amber-600" />;
};

const statusBadgeCls = (s) => {
  if (s === "sent") return "bg-green-50 text-green-700 border-green-200";
  if (s === "failed") return "bg-red-50 text-red-700 border-red-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
};

export default function EmailLogs() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("all");
  const [resending, setResending] = useState(null);
  const [actionMsg, setActionMsg] = useState("");
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (status !== "all") params.status = status;
      const { data } = await adminApi.get("/admin/email-logs", { params });
      setRows(data.logs || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleResend = async (regId, toEmail) => {
    if (!window.confirm(`Resend confirmation email to ${toEmail}?`)) return;
    setResending(regId);
    try {
      await adminApi.post(`/admin/email-logs/resend/${regId}`);
      setActionMsg(`Email queued for ${toEmail}`);
      setTimeout(() => setActionMsg(""), 3000);
      fetchData();
    } catch (e) {
      alert("Resend failed: " + formatApiError(e));
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="email-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
            Email Logs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} email record{total !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 disabled:opacity-60"
          data-testid="refresh-logs"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {actionMsg && (
        <div
          className="px-4 py-2.5 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-center gap-2"
          data-testid="logs-action-message"
        >
          <CheckCircle2 className="w-4 h-4" />
          {actionMsg}
        </div>
      )}
      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <form onSubmit={handleSearch} className="sm:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by recipient, name, or subject..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
              data-testid="logs-search-input"
            />
          </div>
        </form>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
          data-testid="filter-log-status"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "All Statuses" : statusLabels[s] || s}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-[#FF9933] animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">
            <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            No email logs found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-logs-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Recipient", "Subject", "Category", "Status", "Sent At", "Actions"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((lg, i) => (
                  <tr
                    key={lg.reg_id + "_" + i}
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`log-row-${i}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-xs">{lg.to}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{lg.name}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-xs">
                      <div className="truncate">{lg.subject}</div>
                      {lg.error && (
                        <div className="text-xs text-red-600 mt-1 truncate" title={lg.error} data-testid={`log-error-${i}`}>
                          ⚠ {lg.error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 capitalize">
                      {lg.type === "broadcast" ? (
                        <span className="inline-block px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-medium">
                          broadcast
                        </span>
                      ) : lg.type === "smtp_test" ? (
                        <span className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium">
                          test
                        </span>
                      ) : (
                        lg.category || "—"
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadgeCls(
                          lg.status
                        )}`}
                      >
                        {statusIcon(lg.status)}
                        {statusLabels[lg.status] || lg.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {lg.sent_at ? new Date(lg.sent_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleResend(lg.reg_id, lg.to)}
                        disabled={resending === lg.reg_id || !lg.reg_id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-white border border-gray-300 hover:border-[#FF9933] hover:text-[#FF9933] text-gray-700 rounded-md transition-colors disabled:opacity-50"
                        data-testid={`resend-btn-${i}`}
                      >
                        {resending === lg.reg_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        Resend
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <p className="text-xs text-gray-500">
              Page {page} of {pages} · {total} total
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 text-gray-600 hover:text-[#FF9933] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="logs-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-1.5 text-gray-600 hover:text-[#FF9933] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="logs-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
